
import { utc } from "moment";

import { FormatBorderStyle, RenderRG, parser } from 'data/reporting'

import { RentRollCalcData } from "data/calc";

const { parse, sheet, columns, row, freeze, cell, RGB } = parser();

const colors = {
    black: RGB(0, 0, 0),
    ice_white: RGB(255, 255, 255),
    ice_gray: RGB(255, 255, 245),
    chayote_green: RGB(239, 243, 233),
    pale_green: RGB(222, 215, 213),
    sage_green: RGB(209, 219, 189),
    forest_green: RGB(80, 109, 94),
    metal_green: RGB(145, 170, 157),
    metal_blue: RGB(62, 96, 111),
    indigo_blue: RGB(25, 52, 65),
    rusty_red: RGB(169, 68, 66),
    fog_gray: RGB(249, 249, 249),
    disabled_gray: RGB(204, 204, 204),
    pants_gray: RGB(157, 168, 178),
    light_gray: RGB(217, 217, 217),
    pale_yellow: RGB(255, 242, 204),
    pale_orange: RGB(255, 230, 153)
};
/**

*/


export type Params = {
    beginDate: string;
    endDate: string
    periodicity: 1 | 3 | 12;
}


function mNum(date) {
    const match = /(\d\d\d\d)-(\d\d)-(\d\d)/.exec(date);
    if (match) {
        const [whole, year, month, day] = match;
        return 12 * Number(year) + Number(month);
    } else {
        throw new Error();
    }
}

function sum(array) {
    if (!array)
        return 0;
    let total = 0;
    array.forEach((v) => (total += v));
    return total;
}
function sumArrays(array, ...arrays) {
    let total = null as number[] | null;
    [array, ...arrays].forEach(v => v ? (total = (total ? total.map((x, i) => x + v[i]) : v)) : null)
    return total;
}
function timeSeries(
    dataBeginDate,
    params: Params
) {
    const { beginDate, endDate, periodicity } = params
    const topBorder = { borders: { top: { style: "double" as FormatBorderStyle, color: colors.black } } }
    const trueBegin = mNum(beginDate) - mNum(dataBeginDate) //month
    const trueEnd = mNum(endDate) - mNum(dataBeginDate) //month
    const clip = trueBegin < 0 ? Math.floor(-trueBegin / periodicity) : 0 //periods
    const term = trueEnd < trueBegin ? 1 : (trueEnd - trueBegin) + 1 //months
    const stub = trueBegin < 0 ? - (trueBegin % periodicity) : 0 //months
    const length = Math.ceil(term / periodicity) - clip; //periods
    const bMoment = utc(dataBeginDate, "YYYY-MM-DD");
    const indeces = Array(length)
        .fill(0)
        .map((v, i) => trueBegin + (i + clip) * periodicity);
    const monthIndeces = Array(periodicity)
        .fill(0)
        .map((v, i) => i);
    function defineCols(width?: number) {
        const cols = columns(...indeces.map((x) => ({ width })));
        return {
            getCols() {
                return cols;
            },
            populate(data, total = false) {
                return indeces.map((pi, i) =>
                    cell({
                        col: cols[i],
                        value: data ? sum(monthIndeces.map((mi) => (pi + mi >= 0 ? data[pi + mi] : 0))) : 0,
                        format: total ? topBorder : {}
                    })
                );
            },
            dateNums() {
                return indeces.map((pi, i) => {
                    const label = ["", "Month", "", "Quarter", "", "", "", "", "", "", "", "", "Year"][periodicity]
                    return cell({
                        format: { text: { color: colors.ice_gray, align: "center" }, fill: { color: colors.metal_blue } },
                        col: cols[i],
                        value: `${label} ${i + 1}`,
                    })
                });
            },
            dates(eop = false) {
                return indeces.map((pi, i) =>
                    cell({
                        format: { text: { color: colors.metal_blue, align: "center" }, fill: { color: colors.chayote_green } },
                        col: cols[i],
                        value: (eop
                            ? bMoment
                                .clone()
                                .add(pi + periodicity, "month")
                                .add(-1, "day")
                            : bMoment.clone().add(pi + (i == 0 ? stub : 0), "month")
                        ).format("MM/DD/YYYY")
                    })
                );
            },
        };
    }
    return defineCols;
}

export function tenantCashFlow(params: Params, dataBeginDate: string, tenant: RentRollCalcData, lastCalc?: string) {
    const ts = timeSeries(dataBeginDate, params);
    const {
        occupiedArea,
        vacantArea,
        baseRent,
        vacantRent,
        freeRent,
        recoveries,
        freeRecoveries,
        miscRevenue,
        freeMiscRevenue,
        percentageRent,
        freePercentageRent,
        tenantImprovements,
        leasingCommissions,
        otherCapital,
        poolMissingOriginal,
        poolMissingGenerated

    } = tenant.output;
    return parse(RenderRG, () => {
        sheet({
            name: "Tenant Cash Flow",
            width: 75,
            height: 20,
            format: { text: { size: 10 } },
            _() {
                const [c_head, c_desc1, c_desc2, c_desc3, c_desc4, c_desc5, c_desc6] = columns(
                    { width: 30 },
                    { width: 20 },
                    { width: 20 },
                    { width: 20 },
                    { width: 20 },
                    { width: 120 },
                    { width: 72 },
                );
                freeze();
                const cts = ts();

                const conditionalRow = (description, amounts, persist = false) => {
                    if (persist || (sum(amounts) !== 0))
                        return row(() => {
                            cell({ col: c_desc3, value: description });
                            cts.populate(amounts);
                        })
                }
                const totalRow1 = (description, amounts) => {
                    row({ height: 8 })
                    return row({
                        format: { text: { bold: true }, borders: { top: { style: 'single', color: colors.black } } },
                        _() {
                            cell({ col: c_desc4, value: description });
                            cts.populate(amounts);
                        }
                    })
                }
                const totalRow2 = (description, amounts) => {
                    row({ height: 8 })
                    return row({
                        format: { text: { bold: true }, borders: { top: { style: 'double', color: colors.black } } },
                        _() {
                            cell({ col: c_desc2, value: description });
                            cts.populate(amounts);
                        }
                    })
                }
                const totalRow3 = (description, amounts) => {
                    row({ height: 8 })
                    return row({
                        format: { text: { bold: true }, fill: { color: colors.metal_green }, borders: { top: { style: 'double', color: colors.black } } },
                        _() {
                            cell({ col: c_desc1, value: description });
                            cts.populate(amounts);
                        }
                    })
                }

                row({ height: 24, format: { text: { size: 12, bold: true } }, _: () => cell({ col: c_desc1, value: tenant.tenant }) });
                row({ height: 24, format: { text: { size: 12, bold: true } }, _: () => cell({ col: c_desc1, value: `Suite: ${tenant.suite}` }) });

                const poolWarning = poolMissingOriginal ? "Recovery" : poolMissingGenerated ? "Market Recovery" : undefined
                if (poolWarning)
                    row({
                        format: { fill: { color: RGB(192, 0, 0) }, text: { color: colors.ice_white, italic: true } }, _: () => cell({ col: c_desc1, value: `*Some ${poolWarning} Pool results for this tenant have not been calculated; run a full calculation` })
                    })
                else if (lastCalc)
                    row({
                        format: { text: { italic: true } }, _: () => cell({ col: c_desc1, value: `*Recoveries Pool amounts estimated based on last full calc; run a full calculation for exact values` })
                    })


                row(() => {
                    cts.dateNums();
                });
                row(() => {
                    cell({ col: c_desc6, value: "period begin:", format: { text: { italic: true } } });
                    cts.dates();
                });
                row(() => {
                    cell({ col: c_desc6, value: "period end:", format: { text: { italic: true } } });
                    cts.dates(true);
                });
                row({ height: 8 });
                freeze();

                row(() => { cell({ col: c_desc1, value: "CASH FLOW", format: { text: { size: 11 } } }) })
                row({ height: 8 });
                conditionalRow("Potential Base Rent", sumArrays(baseRent, vacantRent), true)
                conditionalRow("Less: Absorption/Turnover Vacancy", vacantRent?.map(v => -v))
                conditionalRow("Less: Abatement", freeRent)

                const sbr = sumArrays(baseRent, freeRent)
                const otr = sumArrays(recoveries, freeRecoveries, percentageRent, freePercentageRent, miscRevenue, freeMiscRevenue);
                (sum(otr) !== 0 ? totalRow1 : totalRow2)("Scheduled Base Rent", sbr)
                row({ height: 8 })

                let r1 = conditionalRow("Recoveries", recoveries)
                r1 = conditionalRow("Recoveries - Abatement", freeRecoveries) || r1
                r1 = conditionalRow("Percentage Rent", percentageRent) || r1
                r1 = conditionalRow("Percentage Rent - Abatement", freePercentageRent) || r1
                r1 = conditionalRow("Misc. Revenue", miscRevenue) || r1
                r1 = conditionalRow("Misc. Revenue - Abatement", freeMiscRevenue) || r1
                const ttr = sumArrays(sbr, recoveries, freeRecoveries, percentageRent, freePercentageRent, miscRevenue, freeMiscRevenue)
                if (r1) {
                    totalRow2("Total Tenant Revenue", ttr)
                    row({ height: 8 })
                }

                conditionalRow("Tenant Improvements", tenantImprovements, true)
                conditionalRow("Leasing Commissions", leasingCommissions, true)
                conditionalRow("Other Tenant Capital", otherCapital)

                const tcx = sumArrays(tenantImprovements, leasingCommissions, otherCapital)
                totalRow2("Total Capital", tcx)

                const tcf = sumArrays(ttr, tcx?.map(v => -v))
                totalRow3("Tenant Cash Flow", tcf)

            },
        });

    });
}
